import { CircleAlertIcon, ShieldCheckIcon } from 'lucide-react';
import type { JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import type { GenerationBasketPreview } from '@/lib/api/quality-generation-v4.js';
import type {
  CoverageWarning,
  GenerationMetaSnapshot,
  RequirementCoverageStatus,
} from '@/lib/api/quality-types.js';

const STATUS_LABEL: Record<RequirementCoverageStatus, string> = {
  covered: 'Covered',
  partial: 'Partially covered',
  uncovered: 'Uncovered',
};

type Destination = GenerationMetaSnapshot['destination'];
type CoverageGuidance = Pick<
  CoverageWarning,
  'message' | 'suggestedFollowUp' | 'uncoveredRequirementIds'
>;

function nonEmptyLabel(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function sourceLabel(meta: GenerationMetaSnapshot): string {
  const source = meta.selectedJiraSource;
  if (!source) return 'No Jira requirement source was selected';

  const rootIssueKey = nonEmptyLabel(source.rootIssueKey);
  const issueKeys = source.issueKeys
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
  const orderedKeys = rootIssueKey
    ? [rootIssueKey, ...issueKeys.filter((key) => key !== rootIssueKey)]
    : issueKeys;
  const visibleKeys = orderedKeys.slice(0, 3);
  const remaining = orderedKeys.length - visibleKeys.length;
  const issueIdentity = visibleKeys.length
    ? `${visibleKeys.join(', ')}${remaining > 0 ? ` +${remaining} more` : ''}`
    : null;
  const projectIdentity =
    nonEmptyLabel(source.projectName) ?? nonEmptyLabel(source.projectKey);

  if (projectIdentity && issueIdentity) {
    return `${projectIdentity} · ${issueIdentity}`;
  }
  return (
    projectIdentity ??
    issueIdentity ??
    (source.sourceKind === 'jira_query' ? 'Jira query' : 'Jira issue')
  );
}

function destinationBadgeVariant(
  destination: Destination,
): 'success' | 'warning' | 'destructive' {
  switch (destination.lifecycleState) {
    case 'active':
      return 'success';
    case 'unknown':
      return 'warning';
    default:
      return 'destructive';
  }
}

function destinationBadgeLabel(destination: Destination): string {
  switch (destination.lifecycleState) {
    case 'active':
      return 'Active destination';
    case 'unknown':
      return 'Validation pending';
    default:
      return destination.lifecycleState;
  }
}

function caseLimitLabel(maxCases: number | null | undefined): string {
  if (maxCases == null) return 'Service-selected limit';
  return `${maxCases} ${maxCases === 1 ? 'case' : 'cases'} (absolute maximum)`;
}

function derivedCoverageGuidance(
  meta: GenerationMetaSnapshot,
): CoverageGuidance | null {
  const { coverageSummary } = meta;
  if (coverageSummary.assessment !== 'known') return null;
  if (
    coverageSummary.partialRequirements === 0 &&
    coverageSummary.uncoveredRequirements === 0
  ) {
    return null;
  }

  return {
    message: 'Some requirements are not fully covered by these cases.',
    suggestedFollowUp:
      'Generate more cases later to improve requirement coverage.',
    uncoveredRequirementIds: meta.requirementInventory
      .filter(({ coverageStatus }) => coverageStatus !== 'covered')
      .map(({ requirementId }) => requirementId),
  };
}

export function isGenerationDestinationPublishable(
  meta: GenerationMetaSnapshot | null | undefined,
): boolean {
  if (!meta) return true;
  return (
    meta.destination.lifecycleState === 'active' ||
    meta.destination.lifecycleState === 'unknown'
  );
}

export function GenerationMetaReview({
  meta,
  coverage,
  selectedCoverage = false,
}: {
  meta: GenerationMetaSnapshot | null | undefined;
  coverage?: GenerationBasketPreview['coverage'];
  selectedCoverage?: boolean;
}): JSX.Element {
  if (!meta) {
    return (
      <section
        role="status"
        aria-label="Coverage status"
        className="text-[11px] text-muted-foreground"
      >
        <span className="font-medium">Coverage data unavailable.</span> This
        proposal predates the requirement inventory. Saved cases remain
        available for review.
      </section>
    );
  }

  if (selectedCoverage)
    meta = {
      ...meta,
      requirementInventory: coverage?.inventory ?? [],
      coverageSummary: coverage?.summary ?? {
        assessment: 'unknown',
        reason: 'independent_review_unavailable',
        message: 'Coverage for selected cases is updating.',
      },
      coverageWarning: null,
    };

  const {
    coverageSummary,
    coverageWarning,
    destination,
    metadataWarnings = [],
  } = meta;
  const destinationUnknown = destination.lifecycleState === 'unknown';
  const destinationBlocked = !isGenerationDestinationPublishable(meta);
  const coverageGuidance = coverageWarning ?? derivedCoverageGuidance(meta);

  return (
    <section
      aria-label="Generation review metadata"
      className="flex flex-col gap-2 text-[12px]"
    >
      <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
        {destination.provider === 'own-tms' ? (
          <p className="text-foreground">
            {`Stored in Quality · ${destination.containerName ?? 'Quality suite'}`}
          </p>
        ) : (
          <>
            <p className="text-muted-foreground">
              <span className="text-foreground">{destination.projectName}</span>
              {destination.containerName
                ? ` · ${destination.containerName}`
                : ''}
            </p>
          </>
        )}
        {destination.lifecycleState !== 'active' ? (
          <Badge variant={destinationBadgeVariant(destination)}>
            {destinationBadgeLabel(destination)}
          </Badge>
        ) : null}
      </div>

      {destinationUnknown ? (
        <div
          role="status"
          aria-label="Destination validation"
          className="flex items-start gap-2 rounded-md border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-warning-700"
        >
          <ShieldCheckIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Destination status will be validated live when you publish. If the
            destination is unsafe, the service will stop the write.
          </span>
        </div>
      ) : null}

      {destinationBlocked ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive"
        >
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            This destination is {destination.lifecycleState}. Select or
            configure another active destination before the next publication
            attempt.
          </span>
        </div>
      ) : null}

      {meta.selectedJiraSource || meta.maxCases != null ? (
        <details className="text-[11px] text-muted-foreground">
          <summary className="cursor-pointer">Generation settings</summary>
          <div className="mt-1 flex flex-col gap-1">
            {meta.selectedJiraSource ? <p>{sourceLabel(meta)}</p> : null}
            {meta.maxCases != null ? (
              <p>{caseLimitLabel(meta.maxCases)}</p>
            ) : null}
          </div>
        </details>
      ) : null}

      {metadataWarnings.length > 0 ? (
        <div
          role="status"
          aria-label="Metadata mapping warnings"
          className="flex items-start gap-2 rounded-md border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-warning-700"
        >
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="flex flex-col gap-1">
            <span>
              <span className="font-medium">
                Some metadata values will be omitted.
              </span>{' '}
              They are not configured in the destination. This does not block
              publishing.
            </span>
            <ul className="list-disc pl-4 text-[11px]">
              {metadataWarnings.map((warning) => (
                <li
                  key={`${warning.caseIndex}:${warning.field}:${warning.value}`}
                >
                  Case #{warning.caseIndex + 1} · {warning.field}: &ldquo;
                  {warning.value}&rdquo; — {warning.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {coverageSummary.assessment === 'unknown' ? (
        <div
          role="status"
          aria-label="Coverage status"
          className="text-[11px] text-muted-foreground"
        >
          <span>Coverage not assessed.</span> {coverageSummary.message}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldCheckIcon className="size-4" aria-hidden />
            <span className="font-medium">
              {selectedCoverage
                ? 'Selected case coverage'
                : 'Requirement coverage'}
            </span>
            <Badge variant="outline">
              {coverageSummary.coveredRequirements} covered
            </Badge>
            {coverageSummary.partialRequirements > 0 ? (
              <Badge variant="warning">
                {coverageSummary.partialRequirements} partial
              </Badge>
            ) : null}
            {coverageSummary.uncoveredRequirements > 0 ? (
              <Badge variant="warning">
                {coverageSummary.uncoveredRequirements} uncovered
              </Badge>
            ) : null}
          </div>

          <div className="overflow-x-auto">
            <table
              aria-label="Requirement coverage"
              className="w-full border-collapse text-left text-[11px]"
            >
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th scope="col" className="px-2 py-1 font-medium">
                    Requirement
                  </th>
                  <th scope="col" className="px-2 py-1 font-medium">
                    Source
                  </th>
                  <th scope="col" className="px-2 py-1 font-medium">
                    Status
                  </th>
                  <th scope="col" className="px-2 py-1 font-medium">
                    Cases
                  </th>
                </tr>
              </thead>
              <tbody>
                {meta.requirementInventory.map((requirement) => (
                  <tr
                    key={requirement.requirementId}
                    className="border-b border-border/60 last:border-b-0"
                  >
                    <td className="px-2 py-1.5">
                      {requirement.requirementText}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground">
                      {requirement.sourceIssueKey}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {STATUS_LABEL[requirement.coverageStatus] ?? 'Unknown'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground">
                      {requirement.caseIndices.length
                        ? requirement.caseIndices
                            .map((index) => `#${index + 1}`)
                            .join(', ')
                        : 'None'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {coverageGuidance ? (
        <div
          role="status"
          aria-label="Coverage guidance"
          className="flex items-start gap-2 rounded-md border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-warning-700"
        >
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span className="flex flex-col gap-1">
            <span>
              <span className="font-medium">{coverageGuidance.message}</span>{' '}
              {coverageGuidance.suggestedFollowUp}
            </span>
            {coverageGuidance.uncoveredRequirementIds.length ? (
              <span className="text-[11px]">
                Requirements needing more coverage:{' '}
                {coverageGuidance.uncoveredRequirementIds.join(', ')}
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
    </section>
  );
}
